import { useLazyQuery, useMutation } from '@apollo/react-hooks';
import cn from 'classnames';
import _ from 'lodash';
import { useRouter } from 'next/router';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useBookingProcessSelectBedContenxt } from '~components/booking-process/select-bed/booking-process-select-bed-context';
import Layout from '~components/layout';
import { useToast } from '~components/toast/use-toast';
import {
  BookingStatusOption,
  StudentPayToOptions,
  TenancyLengthOptions,
  TenancyOptionRateTypeOption,
} from '~graphql/__generated__/globalTypes';
import {
  QueryPropertyAcademicYearFlexibilityControlInfo,
  QueryPropertyAcademicYearFlexibilityControlInfo_node_Property_academicYear_current_flexibilityControlInfo,
  QueryPropertyAcademicYearFlexibilityControlInfoVariables,
} from '~graphql/__generated__/QueryPropertyAcademicYearFlexibilityControlInfo';
import {
  SP_MutationStudentCancelBookingCountDown,
  SP_MutationStudentCancelBookingCountDownVariables,
} from '~graphql/__generated__/SP_MutationStudentCancelBookingCountDown';
import { SP_QueryNodeProperty_node_Property } from '~graphql/__generated__/SP_QueryNodeProperty';
import {
  SP_QueryStudentGetSelectedFlatInfo,
  SP_QueryStudentGetSelectedFlatInfoVariables,
} from '~graphql/__generated__/SP_QueryStudentGetSelectedFlatInfo';
import {
  SP_Student_Get_Booking,
  SP_Student_Get_Booking_studentGetBooking,
  SP_Student_Get_BookingVariables,
} from '~graphql/__generated__/SP_Student_Get_Booking';
import { STUDENT_GET_BOOKING } from '~graphql/booking';
import { QUERY_NODE_TENDENCY } from '~graphql/filter';
import { MUTATION_STUDENT_EDIT_BOOKING, QUERY_NODE_BED } from '~graphql/payment-plan';
import { MUTATION_STUDENT_CANCEL_BOOKING_COUNTDOWN } from '~graphql/person-detail';
import {
  QUERY_NODE_PROPERTY,
  QUERY_PROPERTY_ACADEMIC_YEAR_FLEXIBILITY_CONTROL_INFO,
  QUERY_SELECT_FALT_INFO,
} from '~graphql/select-bed';
import { Router, useTranslation } from '~i18n';
import { displayWarnToast, modalDisappear } from '~store/modal';
import { formatToUpper } from '~utils/js-helper';
import { unifiedNavigate } from '~utils/unifiedNavigate';
import { gtmDataLayer } from '~utils/use-checkout-stage';
import { usePropertyIsOffline } from '~utils/use-property-offline-status';
import { BookingCompletedStatus } from '~utils/use-status';
import ErrorPage from '../../pages/_error';

export interface IDateProps {
  startDate: Date | undefined;
  endDate: Date | undefined;
}

export interface BookingPaymentParamsProps {
  heldToTime: string | undefined | null;
  totalPriceOriginal: number | undefined | null;
  pricePerNightOriginal: number | undefined | null;
  pricePerMonthOriginal: number | undefined | null;
  bedId: string | undefined | null;
}

interface IBookingProcessProps {
  booking: SP_Student_Get_Booking_studentGetBooking | null | undefined;
  setBooking: any;
  propertyAcademicYearFlexibilityControlInfo: QueryPropertyAcademicYearFlexibilityControlInfo_node_Property_academicYear_current_flexibilityControlInfo | null;
  property: SP_QueryNodeProperty_node_Property | null | undefined;
  setProperty: any;
  progressStatus: BookingStatusOption | null | undefined;
  setProgressStatus: any;
  bookingRelatedIds?: any;
  setBookingRelatedIds?: any;
  tenancyOptionRateType: TenancyOptionRateTypeOption;

  setTenancyOptionRateType: any;

  tenancyLengthOption: TenancyLengthOptions | undefined | null;
  setTenancyLengthOption: any;

  tenancyOptionName: string;
  setTenancyOptionName: any;
  roomTypeName: string;
  setRoomTypeName: any;
  bedName: string;
  setBedName: any;
  setBookingPaymentParams: any;
  bookingPaymentParams: BookingPaymentParamsProps;

  editBooking?: Function;
  editBookingConfirm?: Function;
  cancelBookingCountDown?: Function;
  countDownFinish?: Function;
  cancelCountDownLoading?: boolean;

  handleRefetchBooking?: Function;

  isPayToPartner: boolean;
  isRollingBooking: boolean;
}

export const initialBookingPaymentParams = {
  heldToTime: '',
  totalPriceOriginal: 0,
  pricePerNightOriginal: 0,
  pricePerMonthOriginal: 0,
  bedId: '',
};

const BookingProcessContext = createContext<IBookingProcessProps>({
  booking: null,
  setBooking: () => {},
  propertyAcademicYearFlexibilityControlInfo: null,
  property: null,
  setProperty: () => {},
  progressStatus: null,
  setProgressStatus: () => {},
  tenancyOptionRateType: TenancyOptionRateTypeOption.NIGHTLY,
  setTenancyOptionRateType: () => {},

  tenancyLengthOption: null,
  setTenancyLengthOption: () => {},

  roomTypeName: '',
  setRoomTypeName: () => {},

  tenancyOptionName: '',
  setTenancyOptionName: () => {},

  bedName: '',
  setBedName: () => {},
  setBookingPaymentParams: () => {},

  bookingPaymentParams: initialBookingPaymentParams,
  isPayToPartner: false,
  isRollingBooking: false,
});

export const BookingProcessProvider: React.FC = ({ children }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { addToast } = useToast();
  const { t } = useTranslation(['student_portal']);
  const {
    propertyId: initialPropertyId,
    bookingId,
    initialRoomTypeId,
    initialTenancyOptionId,
    initialAcademicYearId,
    initialTenancyEndDate,
    initialTenancyStartDate,
    initialBedId: urlBedId,
  } = router.query;

  const {
    setSelectBedRoomTypeId,
    setSelectBedTenancyOptionId,
    setSelectBedAcademicYearId,
    setSelectBedTenancyDate,
    initEditBookingInput,
  } = useBookingProcessSelectBedContenxt();

  const [booking, setBooking] = useState<
    SP_Student_Get_Booking_studentGetBooking | null | undefined
  >();
  const [property, setProperty] = useState<SP_QueryNodeProperty_node_Property | null | undefined>();
  const [progressStatus, setProgressStatus] = useState<BookingStatusOption>(
    urlBedId ? BookingStatusOption.ROOM_SELECTED : BookingStatusOption.APPLIED
  );
  const [tenancyOptionRateType, setTenancyOptionRateType] = useState<TenancyOptionRateTypeOption>(
    TenancyOptionRateTypeOption.NIGHTLY
  );

  const [tenancyLengthOption, setTenancyLengthOption] = useState<
    TenancyLengthOptions | null | undefined
  >();

  const [isPayToPartner, setIsPayToPartner] = useState(false);
  const [tenancyOptionName, setTenancyOptionName] = useState('');
  const [roomTypeName, setRoomTypeName] = useState('');
  const [bedName, setBedName] = useState('');

  const [bookingPaymentParams, setBookingPaymentParams] = useState<BookingPaymentParamsProps>(
    initialBookingPaymentParams
  );

  const isRollingBooking = tenancyLengthOption === TenancyLengthOptions.ROLLING;

  const [bookingRelatedIds, setBookingRelatedIds] = useState<any>({
    propertyId: initialPropertyId && initialPropertyId.toString(),
    roomTypeId: initialRoomTypeId && initialRoomTypeId.toString(),
    tenancyOptionId: initialTenancyOptionId && initialTenancyOptionId.toString(),
    academicYearId: initialAcademicYearId && initialAcademicYearId.toString(),
    tenancyEndDate: initialTenancyEndDate && initialTenancyEndDate.toString(),
    tenancyStartDate: initialTenancyStartDate && initialTenancyStartDate.toString(),
  });

  const [editBooking] = useMutation(MUTATION_STUDENT_EDIT_BOOKING, {
    variables: {
      input: {
        id: bookingId,
        roomTypeId: bookingRelatedIds.roomTypeId,
        academicYearId: bookingRelatedIds.academicYearId,
        tenancyOptionId: bookingRelatedIds.tenancyOptionId,
        tenancyStartDate: bookingRelatedIds.tenancyStartDate,
        tenancyEndDate: bookingRelatedIds.tenancyEndDate,
        tenancyOptionRateType,
      },
    },
    onCompleted: ({ studentEditBooking }) => {
      dispatch(modalDisappear());
      if (studentEditBooking.id) {
        setProgressStatus(studentEditBooking?.status);
        setBooking((prevState: any) => {
          return {
            ...prevState,
            countDown: null,
          };
        });
      }
    },
    onError: ({ graphQLErrors }) => {
      dispatch(modalDisappear());
      const { message } = graphQLErrors[0] || {};
      addToast(
        t('error.message', {
          errorMsg: `(${formatToUpper(message)})`,
        })
      );
    },
  });

  const [studentCancelBookingCountDown, { loading: cancelCountDownLoading }] = useMutation<
    SP_MutationStudentCancelBookingCountDown,
    SP_MutationStudentCancelBookingCountDownVariables
  >(MUTATION_STUDENT_CANCEL_BOOKING_COUNTDOWN, {
    onCompleted: data => {
      dispatch(modalDisappear());
      if (data?.studentCancelBookingCountDown?.status) {
        setProgressStatus(data?.studentCancelBookingCountDown?.status);
        setBooking((prevState: any) => {
          return {
            ...prevState,
            countDown: null,
          };
        });
      }
    },
    onError: ({ graphQLErrors }) => {
      const { message } = graphQLErrors[0] || {};
      addToast(
        t('error.message', {
          errorMsg: `(${formatToUpper(message)})`,
        })
      );
    },
  });

  const [
    queryPropertyAcademicYearFlexibilityControlInfo,
    { data: propertyAcademicYearFlexibilityControlInfoResult },
  ] = useLazyQuery<
    QueryPropertyAcademicYearFlexibilityControlInfo,
    QueryPropertyAcademicYearFlexibilityControlInfoVariables
  >(QUERY_PROPERTY_ACADEMIC_YEAR_FLEXIBILITY_CONTROL_INFO);

  const [queryProperty] = useLazyQuery(QUERY_NODE_PROPERTY, {
    onCompleted: ({ node: property }) => {
      setProperty(property);
      queryPropertyAcademicYearFlexibilityControlInfo({
        variables: {
          id: property?.id,
        },
      });
    },
  });

  const [getFlatBeds] = useLazyQuery<
    SP_QueryStudentGetSelectedFlatInfo,
    SP_QueryStudentGetSelectedFlatInfoVariables
  >(QUERY_SELECT_FALT_INFO, {
    onCompleted: allflat => {
      let selectedFlats = allflat?.studentGetSelectedFlatInfo?.beds?.filter(
        bd => bd?.bedId === urlBedId
      );
      if (selectedFlats && selectedFlats.length > 0) {
        let selectedFlat = selectedFlats[0];
        if (selectedFlat?.pricePerNightOriginal) {
          setBookingPaymentParams({
            ...bookingPaymentParams,
            bedId: selectedFlat?.bedId,
            totalPriceOriginal: selectedFlat?.totalPriceOriginal,
            pricePerNightOriginal: selectedFlat?.pricePerNightOriginal,
            pricePerMonthOriginal: selectedFlat?.pricePerMonthOriginal,
          });
        } else {
          dispatch(
            displayWarnToast({
              isOpen: true,
              title: t('modal.bed_unavailable.title'),
              content: t('modal.bed_unavailable.label'),
              btnText: t('modal.bed_unavailable.btn'),
              onConfirm: () => {
                dispatch(modalDisappear());
                window.location.href = window.location.href.replace(
                  `&initialBedId=${urlBedId}`,
                  ''
                );
              },
            })
          );
        }
      }
    },
  });

  const [queryBooking, { refetch: refetchBooking }] = useLazyQuery<
    SP_Student_Get_Booking,
    SP_Student_Get_BookingVariables
  >(STUDENT_GET_BOOKING, {
    fetchPolicy: 'no-cache',
    onCompleted: ({ studentGetBooking }) => {
      const {
        id,
        status,
        heldToTime,
        tenancyOptionRateType,
        tenancyLengthOption,
        property,
        roomType,
        academicYear,
        tenancyOption,
        totalPriceAfterService,
        pricePerNightOriginal,
        pricePerMonthOriginal,
        roomAllocation,
        nominationPlan,
      } = studentGetBooking || {};

      if (!status) return;
      if (BookingCompletedStatus.includes(status)) {
        return unifiedNavigate(`/booking-details?bookingId=${id}`);
      }
      if (status === BookingStatusOption.BOOKING_CONFIRMED) {
        return Router.push(`/booking-confirmed?bookingId=${id}`);
      }
      setProgressStatus(status);
      setBooking(studentGetBooking);
      setTenancyOptionName(tenancyOption?.name || '');
      setBookingRelatedIds({
        propertyId: property?.id,
        roomTypeId: roomType?.roomTypeId,
        academicYearId: academicYear?.id,
        tenancyOptionId: tenancyOption?.tenancyOptionId,
        tenancyEndDate: tenancyOption?.endDate,
        tenancyStartDate: tenancyOption?.startDate,
      });
      setBookingPaymentParams({
        heldToTime: heldToTime,
        totalPriceOriginal: totalPriceAfterService,
        bedId: roomAllocation?.bedId,
        pricePerNightOriginal,
        pricePerMonthOriginal,
      });

      initEditBookingInput.current = {
        academicYearId: academicYear?.id,
        tenancyOptionId: tenancyOption?.tenancyOptionId,
        tenancyStartDate: tenancyOption?.startDate,
        tenancyEndDate: tenancyOption?.endDate,
        roomTypeId: roomType?.roomTypeId,
      };
      if (roomType?.roomTypeId) setSelectBedRoomTypeId(roomType?.roomTypeId);
      if (academicYear?.id) setSelectBedAcademicYearId(academicYear?.id);
      if (tenancyOption?.tenancyOptionId) {
        setSelectBedTenancyOptionId(tenancyOption?.tenancyOptionId);
      }
      if (tenancyOption?.startDate && tenancyOption?.endDate) {
        setSelectBedTenancyDate({
          startDate: tenancyOption?.startDate,
          endDate: tenancyOption?.endDate,
        });
      }

      if (property) {
        if (roomType?.roomTypeId) {
          queryProperty({
            variables: {
              id: property?.id,
              roomTypeId: roomType?.roomTypeId,
            },
          });
        } else {
          queryPropertyAcademicYearFlexibilityControlInfo({
            variables: {
              id: property?.id,
            },
          });
          setProperty(property as any);
        }
      }

      if (nominationPlan?.studentPayTo === StudentPayToOptions.PARTNER) setIsPayToPartner(true);
      if (tenancyOptionRateType) setTenancyOptionRateType(tenancyOptionRateType);
      if (tenancyLengthOption) setTenancyLengthOption(tenancyLengthOption);
    },
    onError: ({ graphQLErrors }) => {
      const { message } = graphQLErrors[0] || {};
      addToast(t('error.message', { errorMsg: `(${formatToUpper(message)})` }));
    },
  });

  const [queryBed, { data: queryBedResult }] = useLazyQuery(QUERY_NODE_BED);
  const [queryNodeTenancyOption] = useLazyQuery(QUERY_NODE_TENDENCY, {
    onCompleted: data => {
      if (data?.node?.rateType) {
        setTenancyOptionRateType(data?.node?.rateType);
        setTenancyLengthOption(data?.node?.tenancyLengthOption);
        queryBed({ variables: { id: urlBedId } });
      }
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [progressStatus]);

  useEffect(() => {
    if (urlBedId) {
      queryNodeTenancyOption({ variables: { id: initialTenancyOptionId } });
    }
  }, [initialTenancyOptionId, queryNodeTenancyOption, urlBedId]);

  useEffect(() => {
    if (initialPropertyId && initialRoomTypeId && !bookingId && !property) {
      queryProperty({
        variables: {
          id: initialPropertyId && initialPropertyId.toString(),
          roomTypeId: initialRoomTypeId && initialRoomTypeId.toString(),
        },
      });
    }
  }, [bookingId, initialPropertyId, initialRoomTypeId, property, queryProperty]);

  useEffect(() => {
    if (!bookingId) return;
    queryBooking({ variables: { id: bookingId && bookingId.toString() } });
  }, [bookingId, queryBooking]);

  useEffect(() => {
    if (urlBedId && property?.id && queryBedResult?.node?.flat?.id) {
      setProgressStatus(BookingStatusOption.ROOM_SELECTED);
      getFlatBeds({
        variables: {
          propertyId: property.id,
          filter: {
            flatId: queryBedResult?.node?.flat?.id,
            roomTypeId: initialRoomTypeId,
            academicYearId: initialAcademicYearId,
            tenancyOptionId: initialTenancyOptionId,
            tenancyStartDate: initialTenancyStartDate,
            tenancyEndDate: initialTenancyEndDate,
            tenancyOptionRateType,
          },
        },
      });
    }
  }, [
    getFlatBeds,
    initialAcademicYearId,
    initialRoomTypeId,
    initialTenancyEndDate,
    initialTenancyOptionId,
    initialTenancyStartDate,
    property,
    queryBedResult,
    tenancyOptionRateType,
    urlBedId,
  ]);

  // GTM
  useEffect(() => {
    if (property?.country?.name) {
      gtmDataLayer({ Country: property?.country?.name, contentGroup: 'Checkout' });
    }
  }, [property]);

  const propertyIsOffline = usePropertyIsOffline(property?.status);

  useEffect(() => {
    if (propertyIsOffline) {
      dispatch(
        displayWarnToast({
          isOpen: true,
          title: t('access_restricted'),
          content: t(
            'this_property_has_been_sold_and_is_no_longer_managed_by_us_this_page_is_not_available'
          ),
          btnText: t('modal.to_many_bookings.btn'),
          onConfirm: () => {
            unifiedNavigate(`/my-bookings`);
          },
        })
      );
    }
  }, [propertyIsOffline, dispatch, t]);

  const countDownFinish = useCallback(() => {
    dispatch(
      displayWarnToast({
        isOpen: true,
        title: t('modal.count_down_timeout.title'),
        content: t('booking.time_out.reminder'),
        btnText: t('booking.btn.restart'),
        onConfirm: () => {
          window.location.href = `/booking-process?propertyId=${property?.id}&initialRoomTypeId=${bookingRelatedIds?.roomTypeId}&initialTenancyOptionId=${bookingRelatedIds?.tenancyOptionId}&initialAcademicYearId=${bookingRelatedIds?.academicYearId}&initialTenancyStartDate=${bookingRelatedIds?.tenancyStartDate}&initialTenancyEndDate=${bookingRelatedIds?.tenancyEndDate}`;
          setTimeout(() => {
            dispatch(modalDisappear());
          }, 300);
        },
      })
    );
  }, [bookingRelatedIds, dispatch, property, t]);

  const editBookingConfirm = useCallback(() => {
    dispatch(
      displayWarnToast({
        isOpen: true,
        isShowClose: true,
        title: booking?.specialRateOffered
          ? t('modal.count_down.title_special_rate')
          : t('modal.count_down.title'),
        content: booking?.specialRateOffered
          ? t('modal.count_down.label_special_rate')
          : t('modal.count_down.label'),
        btnText: booking?.specialRateOffered
          ? t('modal.count_down.btn_special_rate')
          : t('modal.count_down.btn'),
        onClose: () => {
          dispatch(modalDisappear());
        },
        onConfirm: () => {
          editBooking();
        },
      })
    );
  }, [booking, dispatch, editBooking, t]);

  const cancelBookingCountDown = useCallback(() => {
    dispatch(
      displayWarnToast({
        isOpen: true,
        isShowClose: true,
        title: booking?.specialRateOffered
          ? t('modal.count_down.title_special_rate')
          : t('modal.count_down.title'),
        content: booking?.specialRateOffered
          ? t('modal.count_down.label_special_rate')
          : t('modal.count_down.label'),
        btnText: booking?.specialRateOffered
          ? t('modal.count_down.btn_special_rate')
          : t('modal.count_down.btn'),
        onClose: () => {
          dispatch(modalDisappear());
        },
        onConfirm: () => {
          if (booking?.id) {
            studentCancelBookingCountDown({
              variables: {
                bookingId: booking?.id,
              },
            });
          }
        },
      })
    );
  }, [booking, dispatch, studentCancelBookingCountDown, t]);

  const handleRefetchBooking = useCallback(() => {
    if (!bookingId) return;
    refetchBooking({
      id: bookingId.toString(),
    }).then(({ data }) => {
      const booking = data.studentGetBooking;
      setBooking(booking);
    });
  }, [bookingId, refetchBooking]);

  if (!(initialPropertyId && initialRoomTypeId && initialAcademicYearId) && !bookingId) {
    return <ErrorPage statusCode={404} />;
  }

  const bookingStatusIsExpired = progressStatus === BookingStatusOption.EXPIRED;

  return (
    <BookingProcessContext.Provider
      value={{
        booking,
        setBooking,
        bookingPaymentParams,
        propertyAcademicYearFlexibilityControlInfo: null,
        property,
        setProperty,
        progressStatus,
        setProgressStatus,
        bookingRelatedIds,
        setBookingRelatedIds,

        tenancyOptionRateType,
        setTenancyOptionRateType,

        tenancyOptionName,
        setTenancyOptionName,

        tenancyLengthOption,
        setTenancyLengthOption,

        roomTypeName,
        setRoomTypeName,

        bedName,
        setBedName,

        setBookingPaymentParams,

        editBookingConfirm,
        editBooking,
        cancelBookingCountDown,
        countDownFinish,
        cancelCountDownLoading,

        handleRefetchBooking,

        isPayToPartner,
        isRollingBooking,
      }}
    >
      <Layout
        className={cn({ 'booking-process-layout': bookingStatusIsExpired })}
        layoutFlexible={bookingStatusIsExpired}
        hiddenLoading={progressStatus === BookingStatusOption.ENQUIRY}
      >
        {property && children}
      </Layout>
    </BookingProcessContext.Provider>
  );
};

export const useBookingProcessContenxt = () => useContext(BookingProcessContext);
